"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AnimatePresence, motion } from "motion/react";
import { ChevronLeft, ChevronRight, Search, X } from "lucide-react";
import { Icon } from "@iconify-icon/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  getLanguagesSectionSchema,
  type LanguagesFormData,
} from "../../../schemas/profileEditSchemas";

import { toast } from "@/utils/customToast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { useProfileDataStore } from "@/features/profile/tutor/store/useProfileDataStore";
import { cn } from "@/lib/utils";
import { API_ENDPOINTS } from "@/constants/api";
import { axiosApiInstance } from "@/libs/axios";
import { editTutorProfile } from "@/features/profile/tutor/api/editProfile";
import { getLanguageFromLocale } from "@/features/profile/utils/profileUtils";
import { InformationTooltip } from "@/components/InformationTooltip";
import { useProfileEditFooterStore } from "@/features/profile/tutor/store/useProfileEditFooterStore";

interface LanguagesEditProps {
  onCancel: () => void;
  onSave: () => void;
}

interface Language {
  id: string;
  name: string;
}

interface LangResponse {
  data: Language[];
}

interface LanguageData {
  id: string;
  name: string;
  proficiency_slug: string;
  proficiency_label: string;
}

// Simplified view states - only two steps now
enum ViewState {
  ALL_LANGUAGES = 0,
  PROFICIENCY_SELECTION = 1,
}

const PROFICIENCY_LEVELS = [
  { id: "beginner", label: "Beginner", icon: "icon-park-solid:pie-two" },
  { id: "conversational", label: "Intermediate", icon: "icon-park-solid:pie-four" },
  { id: "fluent", label: "Fluent", icon: "icon-park-solid:pie-six" },
  { id: "native", label: "Native", icon: "material-symbols:circle" },
];

export function LanguagesEdit({ onCancel, onSave }: LanguagesEditProps) {
  const { profileData, setToReload } = useProfileDataStore();
  const { t } = useTranslation(["profile", "common"], {
    lng: getLanguageFromLocale(profileData?.header_section.locale_code),
  });
  const [languages, setLanguages] = useState<Language[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  // Start directly with ALL_LANGUAGES view
  const [currentView, setCurrentView] = useState<ViewState>(ViewState.ALL_LANGUAGES);
  const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(null);
  const [selectedProficiency, setSelectedProficiency] = useState<string>("");

  // Local state to track changes before saving
  const [localLanguages, setLocalLanguages] = useState<LanguageData[]>([]);
  const setFooter = useProfileEditFooterStore((s) => s.setFooter);

  const form = useForm<LanguagesFormData>({
    resolver: zodResolver(getLanguagesSectionSchema(t)),
    defaultValues: {
      languages: [],
    },
  });

  // Initialize local languages from profile data
  useEffect(() => {
    const initialLanguages =
      profileData?.languages_section?.map((lang) => ({
        id: lang.id,
        name: lang.name,
        proficiency_slug: lang.proficiency_slug,
        proficiency_label: lang.proficiency_display,
      })) || [];

    setLocalLanguages(initialLanguages);
  }, [profileData]);

  useEffect(() => {
    getLanguages();
  }, []);

  async function getLanguages() {
    try {
      setIsLoading(true);
      const { data } = await axiosApiInstance.get<LangResponse>(
        API_ENDPOINTS.TUTOR.UTILS.LANGUAGES,
        {
          params: {
            search: "",
            locale_code: profileData?.header_section.locale_code,
          },
        },
      );

      setLanguages(data.data);
    } catch (error) {
      console.error(error);
      toast.error(t("common:error"), {
        description: t("common:errorMessage"),
      });
    } finally {
      setIsLoading(false);
    }
  }

  const handleSubmit = async () => {
    try {
      if (profileData) {
        const reqData = {
          languages: localLanguages.map((lang) => ({
            id: lang.id,
            proficiency: lang.proficiency_slug,
          })),
        };

        await editTutorProfile(profileData.id, "languages", reqData);
        toast.success(t("common:success"), {
          description: t("common:successMessage"),
        });
        onSave();
        setToReload(true);
      }
    } catch (error) {
      console.error(error);
      toast.error(t("common:error"), {
        description: t("common:errorMessage"),
      });
    }
  };

  const handleLanguageChange = (languageId: string, name: string, proficiencySlug: string) => {
    const proficiencyLevel = PROFICIENCY_LEVELS.find((level) => level.id === proficiencySlug);

    setLocalLanguages((prev) => {
      const updated = [...prev];
      const existingIndex = updated.findIndex((l) => l.id === languageId);

      if (existingIndex !== -1) {
        updated[existingIndex] = {
          id: languageId,
          name,
          proficiency_slug: proficiencySlug,
          proficiency_label: proficiencyLevel?.label || "",
        };
      } else {
        updated.push({
          id: languageId,
          name,
          proficiency_slug: proficiencySlug,
          proficiency_label: proficiencyLevel?.label || "",
        });
      }

      return updated;
    });
  };

  const isLanguageSelected = (id: string) => localLanguages.some((language) => language.id === id);

  const getSelectedLanguageData = (id: string): LanguageData | undefined => {
    return localLanguages.find((l) => l.id === id);
  };

  const handleEditProficiency = (language: Language) => {
    const selectedLangData = getSelectedLanguageData(language.id);

    if (selectedLangData) {
      setSelectedLanguage(language);
      setSelectedProficiency(selectedLangData.proficiency_slug);
      setCurrentView(ViewState.PROFICIENCY_SELECTION);
    }
  };

  const handleProficiencySelect = (proficiencyId: string) => {
    if (selectedLanguage) {
      handleLanguageChange(selectedLanguage.id, selectedLanguage.name, proficiencyId);
      setCurrentView(ViewState.ALL_LANGUAGES);
      setSelectedLanguage(null);
      setSelectedProficiency("");
    }
  };

  const handleLanguageSelect = (language: Language) => {
    if (isLanguageSelected(language.id)) {
      // If already selected, edit proficiency
      handleEditProficiency(language);
    } else {
      // If not selected, go to proficiency selection
      setSelectedLanguage(language);
      setSelectedProficiency("");
      setCurrentView(ViewState.PROFICIENCY_SELECTION);
    }
  };

  const navigateBack = () => {
    if (currentView === ViewState.PROFICIENCY_SELECTION) {
      setCurrentView(ViewState.ALL_LANGUAGES);
      setSelectedLanguage(null);
      setSelectedProficiency("");
    }
  };

  const handleCancel = () => {
    // Reset local changes and close dialog
    const initialLanguages =
      profileData?.languages_section?.map((lang) => ({
        id: lang.id,
        name: lang.name,
        proficiency_slug: lang.proficiency_slug,
        proficiency_label: lang.proficiency_display,
      })) || [];

    setLocalLanguages(initialLanguages);
    onCancel();
  };

  // Filter languages based on search term
  const filteredLanguages = languages.filter(
    (language) => search.length === 0 || language.name.toLowerCase().includes(search.toLowerCase()),
  );

  // Sort languages: selected first, then alphabetically
  const sortedLanguages = [...filteredLanguages].sort((a, b) => {
    const aSelected = isLanguageSelected(a.id);
    const bSelected = isLanguageSelected(b.id);

    if (aSelected && !bSelected) return -1;
    if (!aSelected && bSelected) return 1;

    return a.name.localeCompare(b.name);
  });

  // Page transitions with opacity only
  const pageTransitionVariants = {
    initial: {
      opacity: 0,
    },
    animate: {
      opacity: 1,
      transition: { duration: 0.2, ease: "easeOut" },
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.15, ease: "easeIn" },
    },
  };

  // Staggered item animations
  const listItemVariants = {
    hidden: { opacity: 0, y: 8 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.2,
        ease: "easeOut",
        delay: i * 0.02,
      },
    }),
  };

  // Render all languages view (now wrapped in Card with background effects)
  const renderAllLanguagesView = () => {
    return (
      <motion.div
        key="all-languages"
        animate="animate"
        className="flex h-full flex-col"
        exit="exit"
        initial="initial"
        variants={pageTransitionVariants}
      >
        <Card className="bg-side-accent/80 border-border/30 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-12 -right-16 h-40 w-40 rounded-full bg-gradient-to-br from-violet-400 to-blue-500 opacity-10" />
          <div className="pointer-events-none absolute -bottom-10 -left-12 h-28 w-28 rounded-full bg-gradient-to-tr from-emerald-400 to-teal-500 opacity-10" />
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="bg-background/60 flex items-center rounded-xl p-2">
                <Icon
                  className="text-accent-foreground"
                  height="1.125rem"
                  icon="tabler:world"
                  width="1.125rem"
                />
              </div>
              <CardTitle className="text-accent-foreground text-lg">
                {t("profile:languagesSection.cards.list.title", { defaultValue: "Languages" })}
              </CardTitle>
              <InformationTooltip
                description={t("profile:languagesSection.tooltips.list", {
                  defaultValue: "Add the languages you speak and set proficiency.",
                })}
              />
            </div>
            <CardDescription className="text-muted-foreground">
              {t("profile:languagesSection.cards.list.description", {
                defaultValue:
                  "Search and select languages. Tap a selected language to edit proficiency.",
              })}
            </CardDescription>
          </CardHeader>
          <CardContent className="pb-24">
            <div className="relative mb-4 flex-shrink-0">
              <Search className="text-accent-foreground/60 absolute top-3 left-3 h-4 w-4" />
              <Input
                className="pr-10 pl-9"
                placeholder={t("profile:selectLanguages.searchLanguages")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search.length > 0 && (
                <Button
                  className="absolute inset-y-0 right-1 my-auto h-8 w-8"
                  size="icon"
                  type="button"
                  variant="ghost"
                  onClick={() => setSearch("")}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            {isLoading ? (
              <div className="flex h-[300px] w-full items-center justify-center">
                <motion.div
                  animate={{ opacity: 1 }}
                  className="flex flex-col items-center gap-3"
                  initial={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="border-accent-foreground h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
                  <span className="text-muted-foreground text-sm">
                    {t("profile:selectLanguages.loadingLanguages")}
                  </span>
                </motion.div>
              </div>
            ) : sortedLanguages.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 pr-2 pb-2 md:grid-cols-2">
                {sortedLanguages.map((language, index) => {
                  const isSelected = isLanguageSelected(language.id);
                  const selectedData = getSelectedLanguageData(language.id);

                  return (
                    <motion.div
                      key={language.id}
                      animate="visible"
                      custom={index}
                      initial="hidden"
                      variants={listItemVariants}
                    >
                      <Card
                        className={cn(
                          "cursor-pointer transition-all duration-200",
                          isSelected
                            ? "bg-accent-foreground border-accent-foreground"
                            : "hover:bg-accent/5 bg-card",
                        )}
                        onClick={() => handleLanguageSelect(language)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex min-h-[3rem] flex-grow flex-col items-center justify-center text-center">
                              <span
                                className={cn(
                                  "text-base font-medium",
                                  isSelected ? "text-primary" : "text-muted-foreground",
                                )}
                              >
                                {language.name}
                              </span>
                              {isSelected && selectedData && (
                                <p className="text-primary/80 text-xs">
                                  {selectedData.proficiency_label}
                                </p>
                              )}
                            </div>
                            {isSelected ? (
                              <Icon
                                className="text-accent-foreground ml-2"
                                height={20}
                                icon="tabler:check"
                                width={20}
                              />
                            ) : (
                              <ChevronRight className="text-accent-foreground/60 ml-2 h-5 w-5" />
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <motion.div
                animate={{ opacity: 1 }}
                className="flex h-[300px] flex-col items-center justify-center py-12"
                initial={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Icon
                  className="text-muted-foreground mb-2"
                  height={40}
                  icon="tabler:search-off"
                  width={40}
                />
                <span className="text-muted-foreground text-center">
                  {search.length > 0
                    ? t("profile:selectLanguages.noMatchingLanguages")
                    : t("profile:selectLanguages.noLanguagesAvailable")}
                </span>
              </motion.div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  // Render proficiency selection view
  const renderProficiencySelectionView = () => {
    if (!selectedLanguage) return null;

    return (
      <motion.div
        key="proficiency-selection"
        animate="animate"
        className="flex h-full flex-col"
        exit="exit"
        initial="initial"
        variants={pageTransitionVariants}
      >
        <Card className="bg-side-accent/80 border-border/30 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-10 -right-14 h-36 w-36 rounded-full bg-gradient-to-br from-violet-500 to-pink-500 opacity-10" />
          <div className="pointer-events-none absolute -bottom-8 -left-10 h-28 w-28 rounded-full bg-gradient-to-tr from-emerald-400 to-teal-500 opacity-10" />
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="bg-background/60 flex items-center rounded-xl p-2">
                  <Icon
                    className="text-accent-foreground"
                    height="1.125rem"
                    icon="tabler:world"
                    width="1.125rem"
                  />
                </div>
                <CardTitle className="text-accent-foreground text-lg">
                  {selectedLanguage.name}
                </CardTitle>
              </div>
              <Button size="sm" type="button" variant="outline" onClick={navigateBack}>
                <ChevronLeft className="mr-1 h-4 w-4" />
                {t("common:back")}
              </Button>
            </div>
            <CardDescription className="text-muted-foreground">
              {t("profile:selectLanguages.selectProficiency")}
            </CardDescription>
          </CardHeader>

          <CardContent>
            <div className="grid grid-cols-2 gap-4 pr-2 pb-2">
              {PROFICIENCY_LEVELS.map((level, index) => (
                <motion.div
                  key={level.id}
                  animate="visible"
                  custom={index}
                  initial="hidden"
                  variants={listItemVariants}
                >
                  <Card
                    className={cn(
                      "cursor-pointer transition-all duration-200",
                      selectedProficiency === level.id
                        ? "bg-accent-foreground border-accent-foreground"
                        : "hover:bg-accent/5 bg-card",
                    )}
                    onClick={() => setSelectedProficiency(level.id)}
                  >
                    <CardContent className="flex flex-col items-center p-4 text-center">
                      <div className="bg-accent-foreground mb-3 flex h-12 w-12 items-center justify-center rounded-full">
                        <Icon className="text-primary" height={24} icon={level.icon} width={24} />
                      </div>
                      <div
                        className={cn(
                          "text-base font-medium",
                          selectedProficiency === level.id
                            ? "text-primary"
                            : "text-muted-foreground",
                        )}
                      >
                        {t(
                          //@ts-ignore
                          `profile:selectLanguages.proficiencyLevels.${level.id}`,
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  // Render action buttons based on current view
  const renderActionButtons = () => {
    if (currentView === ViewState.ALL_LANGUAGES) {
      return (
        <>
          <Button type="button" variant="outline" onClick={handleCancel}>
            {t("common:cancel")}
          </Button>
          <Button variant="foreground" onClick={handleSubmit}>
            {t("common:save")}
          </Button>
        </>
      );
    } else if (currentView === ViewState.PROFICIENCY_SELECTION) {
      return (
        <>
          <Button type="button" variant="outline" onClick={navigateBack}>
            {t("common:cancel")}
          </Button>
          <Button
            disabled={!selectedProficiency}
            type="button"
            variant="foreground"
            onClick={() => handleProficiencySelect(selectedProficiency)}
          >
            {t("common:confirm")}
          </Button>
        </>
      );
    }

    return null;
  };

  // Register persistent footer content for this section
  useEffect(() => {
    const footerNode = <div className="flex w-full justify-end gap-3">{renderActionButtons()}</div>;

    setFooter({ key: `languages-${currentView}`, node: footerNode });

    return () => {
      // keep stable across quick view/tab changes
    };
  }, [currentView, selectedProficiency, localLanguages]);

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      initial={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3 }}
    >
      <Form {...form}>
        <form
          className="flex h-full flex-col pb-4"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <div className="flex h-full flex-col">
            <FormField
              control={form.control}
              name="languages"
              render={() => (
                <FormItem className="flex h-full flex-col">
                  <FormControl>
                    <div className="flex h-full flex-col">
                      <AnimatePresence mode="wait">
                        {currentView === ViewState.ALL_LANGUAGES && renderAllLanguagesView()}
                        {currentView === ViewState.PROFICIENCY_SELECTION &&
                          renderProficiencySelectionView()}
                      </AnimatePresence>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Footer handled by persistent footer */}
          </div>
        </form>
      </Form>
    </motion.div>
  );
}
